import { createFeature, createReducer, on } from "@ngrx/store";

import { cloneDeep, isUndefined, omitBy } from "lodash";

import { CreateTransactionMutation, FullTransactionFragment, InvoiceWithArtifactsFragment } from "../../generated/graphql.generated";
import { isAegisArtifact } from "../artifacts/artifact.util";
import { deleteEventDialogDeleteEventSuccess } from "../base/mutate-container/mutate-container.component";
import { JOB_ROLE_MAP } from "../global.constants";
import { JobActivityActions } from "../job/job-activity/job-activity.actions";
import { calculateJobTotals } from "../job/job-calculator";
import { CloseJobComponentActions } from "../job/job-create/close-job-component/close-job-component.actions";
import { JobCreateCustomerActions } from "../job/job-create/job-create-customer/job-create-customer.actions";
import { JobCreateLocationsActions } from "../job/job-create/job-create-locations/job-create-locations-state.actions";
import { summaryKeys } from "../job/job-create/job-create-summary/job-create-summary.reducer";
import { JobFormActionButtonActions } from "../job/job-create/job-form-action-button/job-form-action-button.actions";
import { JobEstimateActions } from "../job/job-estimate/job-estimate.actions";
import { JobFinancialsActions } from "../job/job-financials/job-financials.actions";
import { JobHeaderActions } from "../job/job-header/job-header.actions";
import { JobStatusDropdownActions } from "../job/job-header/job-status-dropdown/job-status-dropdown.actions";
import { JobTagActions } from "../job/job-header/job-tags/job-tags.actions";
import { InventoryActions } from "../job/job-inventory/job-inventory.actions";
import { JobInvoiceActions } from "../job/job-invoice/job-invoice.actions";
import { NavigatorOutboxActions } from "../job/job-navigator/navigator-outbox/navigator-outbox.actions";
import { JobAgentActions } from "../job/job-overview/overview-header/job-agent/job-agent.actions";
import { JobLocationsOverviewActions } from "../job/job-overview/overview-locations/overview-locations.actions";
import { OverviewTimelineActions } from "../job/job-overview/overview-timeline/overview-timeline.actions";
import { EventItineraryActions } from "../job/job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.actions";
import { BookEventDialogActions } from "../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.actions";
import { CreateEventDialogActions } from "../job/job-overview/overview-timeline/timeline-create-event/timeline-create-event.actions";
import { JobToolSubscriptionActions } from "../job/job-state/job-tool-subscription.actions";
import { JobToolActions as JobToolV3Actions } from "../job/job-tool.actions";
import { WorkOrdersActions } from "../job/job-workorders/job-workorders.actions";
import { quickJsActions } from "../quickjs/quickjs.actions";
import { calendarEventDetailsUpdateEventSuccess } from "../schedules/calendar-event-details/calendar-event-details.component";
import { DispatchActions } from "../schedules/dispatch/store/dispatch.actions";
import { eventHelperUpdateEventSuccess } from "../services/event-helper.service";
import { CancelAndCloseDialogActions } from "../shared/cancel-and-close-dialog/cancel-and-close-dialog.actions";
import { CreateDocumentDialogActions } from "../shared/create-document-dialog/create-document-dialog.actions";
import { mutateEventDialogUpdateEventSuccess } from "../shared/mutate-calendar-event/mutate-calendar-event.component";
import { loadFromStorage } from "../state/persist-state.utils";
import { currentTimeSeconds } from "../time";
import { CreateTransactionDialogActions } from "../transactions/create-transaction-dialog/create-transaction-dialog.actions";
import { CallState, LoadingState, generateUUID } from "../utilities/state.util";

const jobs = loadFromStorage('jobs', {});

/**
 * The value `startEvent` writes to `navigatorCurrentStep` on launch, before any step is confirmed.
 * The restore path treats it as "no step" and falls through to the steps-map fallback, so an echo
 * re-asserting it after the crew has advanced is how they end up thrown back an arbitrary number of
 * steps. See #46.
 */

export const jobsInitialState = {
  jobs,
  callState: Object.keys(jobs).reduce((curr, jobId) => {
    curr[jobId] = LoadingState.LOADED;
    return curr;
  }, {} as { [jobId: string]: CallState }),
} as const;

export type JobsState = typeof jobsInitialState;

export const jobsFeature = createFeature({
  name: 'Jobs',
  reducer: createReducer(
    jobsInitialState,
    on(
      JobToolV3Actions.fetchJobSuccess,
      CancelAndCloseDialogActions.fetchJobSuccess,
      (state: JobsState, { job }): JobsState => {

          const jobs = cloneDeep(state.jobs);
          jobs[job.id] = job;

          return {
            ...state,
            jobs,
            callState: {
              ...state.callState,
              [job.id]: LoadingState.LOADED,
            }
          }
        },
    ),
    on(JobToolV3Actions.fetchJobError, (state: JobsState, { jobId, error, retry }): JobsState => {

      if (retry) {
        return state;
      }

      return {
        ...state,
        callState: {
          ...state.callState,
          [jobId]: { error },
        }
      }
    }),
    // Metadata-only: the Ops Summary write can race an event change, and
    // this response's events/totals come from a possibly-older snapshot —
    // copying anything besides metadata could overwrite newer cached values
    // (CodeRabbit). saveChangesSuccess keeps the full update below.
    on(
      JobToolV3Actions.updateJobMetadataSuccess,
      (state: JobsState, { updatedJob }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[updatedJob.id];
      // The ops write resolves asynchronously — the user can navigate away
      // and evict the cache entry; a missing job must not throw inside the
      // reducer (CodeRabbit).
      if (!job) {
        return state;
      }

      // jobs.update reads before writing, so the response's metadata can
      // hold stale values for keys the write did not touch (e.g. a newer
      // calculatorUrl). Merge only the field the Ops Summary wrote
      // (CodeRabbit).
      const opsNotesKey = 'calculatorOutput__opsNotes';
      const freshOpsNotes = (updatedJob.metadata ?? {})[opsNotesKey];
      if (freshOpsNotes !== undefined) {
        job.metadata = { ...job.metadata, [opsNotesKey]: freshOpsNotes };
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobToolV3Actions.saveChangesSuccess,
      (state: JobsState, { updatedJob, customerEdit }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[updatedJob.id];

      // Out of an abundance of caution,
      // we will only update properties that we know to be up to date in the response

      job.events = updatedJob.events;
      job.fields = updatedJob.fields;
      job.metadata = updatedJob.metadata;
      job.subTotal = updatedJob.subTotal;
      job.discountTotal = updatedJob.discountTotal;
      job.discountedSubTotal = updatedJob.discountedSubTotal;
      job.taxTotal = updatedJob.taxTotal;
      job.total = updatedJob.total;
      job.locations = updatedJob.locations;
      job.zone = updatedJob.zone;
      job.code = updatedJob.code;
      job.timeline = updatedJob.timeline;
      job.timelineDays = updatedJob.timelineDays;

      for (const key of summaryKeys) {
        job[key] = updatedJob[key];
      }

      // Update local customer
      const customer = job.users.find(user => user?.role === JOB_ROLE_MAP.customerRole);

      if (customerEdit && customer) {

        const {
          selectedCustomer,
          selectedBillingLocation,
          ...rest
        } = customerEdit;

        if (selectedCustomer) {
          customer.user = selectedCustomer;
        }

        const edits = omitBy(rest, isUndefined);

        customer.user = {
          ...customer.user,
          ...edits,
        }

        if (selectedBillingLocation) {
          customer.user.billingAddress = {
            ...selectedBillingLocation,
            id: generateUUID(),
          };
        }
      } else {
        job.users = updatedJob.users;
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobCreateCustomerActions.updateCustomerDataSuccess,
      JobFormActionButtonActions.updateJobSuccess,
      (state: JobsState, action): JobsState => {

        const { jobId, updatedJob, customerEdit } = action;

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        // updateCustomerData now applies customer/profile changes through updateJob,
        // so we can trust updatedJob.users for that action.
        if (action.type === JobFormActionButtonActions.updateJobSuccess.type && customerEdit) {
          const customer = job.users.find(user => user?.role === JOB_ROLE_MAP.customerRole);

          const {
            selectedCustomer,
            selectedBillingLocation,
            ...rest
          } = customerEdit;

          if (selectedCustomer) {
            customer.user = selectedCustomer;
          }

          const edits = omitBy(rest, isUndefined);

          customer.user = {
            ...customer.user,
            ...edits,
          }

          if (selectedBillingLocation) {
            customer.user.billingAddress = {
              ...selectedBillingLocation,
              id: generateUUID(),
            };
          }
        } else {
          job.users = updatedJob.users;
        }

        job.timeline = updatedJob.timeline;
        job.fields = updatedJob.fields;
        job.metadata = updatedJob.metadata;
        job.closedAt = updatedJob.closedAt;
        job.closedReason = updatedJob.closedReason;

        if (action.type === JobFormActionButtonActions.updateJobSuccess.type) {
          job.events = updatedJob.events;
          job.locations = updatedJob.locations;
          job.zone = updatedJob.zone;
          job.code = updatedJob.code;
          job.customerSummary = updatedJob.customerSummary;
          job.crewSummary = updatedJob.crewSummary;
          job.adminSummary = updatedJob.adminSummary;
        }

        return {
          ...state,
          jobs,
        }
      },
    ),

    on(quickJsActions.setScriptOnJobSuccess, quickJsActions.useCalculatorFromZoneSettingsSuccess, (state: JobsState, { jobId, url }) => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      job.metadata = job.metadata || {};
      job.metadata.calculatorUrl = url;

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobCreateLocationsActions.updateLocationsSuccess,
      JobLocationsOverviewActions.addJobLocationSuccess,
      JobLocationsOverviewActions.deleteJobLocationSuccess,
      (state: JobsState, { updatedJob }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[updatedJob.id];
        job.locations = updatedJob.locations;
        job.fields = updatedJob.fields;
        job.metadata = updatedJob.metadata;
        job.zone = updatedJob.zone;
        job.code = updatedJob.code;

        return {
          ...state,
          jobs,
        }
        },
    ),
    on(
      InventoryActions.startOnsiteSuccess,
      InventoryActions.createSelfSurveyLinkSuccess,
      (state: JobsState, { customerLink, employeeLink, jobId }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        job.metadata = {
          ...job.metadata,
          yemboCustomerLink: customerLink,
          yemboEmployeeLink: employeeLink,
        }

        return {
          ...state,
          jobs,
        }
      },
    ),
    on(EventItineraryActions.addLocationToJobSuccess, (state: JobsState, { updatedJob }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[updatedJob.id];

      job.locations = updatedJob.locations;

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobToolV3Actions.updateJobAreaSuccess,
      JobToolV3Actions.importJobToCurrentZoneSuccess,
      (state: JobsState, { updatedJob }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[updatedJob.id];

        job.zone = updatedJob.zone;
        job.code = updatedJob.code;
        job.locations = updatedJob.locations;
        job.metadata = updatedJob.metadata;

        return {
          ...state,
          jobs,
        }
      },
    ),
    on(
      JobFinancialsActions.createTransactionSuccess,
      CreateTransactionDialogActions.createTransactionSuccess,
      CreateTransactionDialogActions.recordPaymentSuccess,
      CreateTransactionDialogActions.payLinkCreated,
      CreateTransactionDialogActions.chargeCardSuccess,
      (state: JobsState, { data }): JobsState => {

      const { transaction } = data.createTransaction;

      const jobId = transaction.job.id;

      const jobs = cloneDeep(addRaisedInvoice(state, data).jobs);

      const job = jobs[jobId];

      const hasTransaction = job.transactions.some((t) => t.id === transaction.id);

      if (hasTransaction) {
        return state;
      }

      job.transactions = [ ...job.transactions, transaction ];

      job.paidTotal = calculateJobTotals(job).paidTotal;

      const invoice = transaction.invoice
        && job.invoices.find((i) => i.id === transaction.invoice.id);

      if (invoice) {

        invoice.paidTotal = transaction.invoice.paidTotal;

        invoice.artifacts = invoice.artifacts.map((artifact) => ({
          ...artifact,
          attributes: [ ...artifact.attributes, 'outdated' ],
        }));

      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobFinancialsActions.updateTransactionSuccess, (state: JobsState, { data }): JobsState => {

      const transaction = data.updateTransaction;

      const jobId = transaction.job.id;

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const transactionIndex = job.transactions.findIndex((t) => t.id === transaction.id);

      job.transactions.splice(transactionIndex, 1, transaction);

      job.paidTotal = calculateJobTotals(job).paidTotal;

      if (transaction.invoice) {

        const invoice = job.invoices.find((i) => i.id === transaction.invoice.id);

        invoice.paidTotal = transaction.invoice.paidTotal;

        invoice.artifacts = invoice.artifacts.map((artifact) => ({
          ...artifact,
          attributes: [ ...artifact.attributes, 'outdated' ],
        }));

      }

      return {
        ...state,
        jobs,
      }
    }),
    // Requirement: both halves apply in one pass, or the totals are recomputed
    // against a job momentarily holding two payable links.
    on(CreateTransactionDialogActions.unsentTransactionCancelled, (state: JobsState, { transaction }): JobsState => {

      const jobId = transaction.job?.id;

      if (!jobId || !state.jobs[jobId]) {
        return state;
      }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const index = job.transactions.findIndex((t) => t.id === transaction.id);

      job.transactions = index >= 0
        ? job.transactions.map((t, i) => (i === index ? transaction : t))
        : [ ...job.transactions, transaction ];

      job.paidTotal = calculateJobTotals(job).paidTotal;

      return {
        ...state,
        jobs,
      }
    }),
    on(CreateTransactionDialogActions.payLinkResent, (state: JobsState, { created, cancelled }): JobsState => {

      const jobId = created.job.id;

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) {
        return state;
      }

      const cancelledIndex = job.transactions.findIndex((t) => t.id === cancelled.id);

      if (cancelledIndex >= 0) {
        job.transactions.splice(cancelledIndex, 1, cancelled);
      }

      // Idempotent by id: a subscription event may have brought this row already.
      if (!job.transactions.some((t) => t.id === created.id)) {
        job.transactions = [ ...job.transactions, created ];
      }

      job.paidTotal = calculateJobTotals(job).paidTotal;

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobFinancialsActions.createDamageSuccess,
      JobFinancialsActions.updateDamageSuccess,
      JobFinancialsActions.removeDamageSuccess,
      (state: JobsState, { updatedJob }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[updatedJob.id];

        job.charges = updatedJob.charges;
        job.events = updatedJob.events;

        job.subTotal = updatedJob.subTotal;
        job.discountTotal = updatedJob.discountTotal;
        job.discountedSubTotal = updatedJob.discountedSubTotal;
        job.taxTotal = updatedJob.taxTotal;
        job.total = updatedJob.total;

        return {
          ...state,
          jobs,
        }
        },
    ),
    on(JobActivityActions.updateCommentSuccess, (state: JobsState, { jobId, comment }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (comment.isReply) {
        const parent = job.comments.comments.find((c) => c.thread.id === comment.thread.id);
        const replyIndex = parent.replies.findIndex((r) => r.id === comment.id);
        parent.replies.splice(replyIndex, 1, comment);
      } else {
        const commentIndex = job.comments.comments.findIndex((c) => c.id === comment.id);
        job.comments.comments.splice(commentIndex, 1, comment);
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobActivityActions.deleteCommentSuccess, (state: JobsState, { jobId, commentId, parentId }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (parentId) {
        const parent = job.comments.comments.find((c) => c.id === parentId);
        parent.replies = parent.replies.filter((r) => r.id !== commentId);
      } else {
        job.comments.comments = job.comments.comments.filter((c) => c.id !== commentId);
        job.comments.total--;
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobToolSubscriptionActions.commentCreated,
      (state: JobsState, { jobId, comment: newComment }): JobsState => {

        if (!jobId) { return state; }

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        if (!job) { return state; }

        if (newComment.isReply) {
          const parent = job.comments.comments.find((c) => c.thread.id === newComment.thread.id);
          if (!parent) { return state; }
          const parentReplies = parent.replies || [];
          parent.replies = [ ...parentReplies, newComment ];
        } else {
          job.comments.comments = [ newComment, ...job.comments.comments ];
        }

        job.comments.total++;

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      JobAgentActions.assignAgentSuccess,
      JobAgentActions.unassignAgentSuccess,
      JobToolV3Actions.autoAssignRolesSuccess,
      (state: JobsState, { updatedJob }): JobsState => {

        if (!updatedJob) { return state; }

        const jobs = cloneDeep(state.jobs);

        const job = jobs[updatedJob.id];

        if (!job) { return state; }

        job.users = updatedJob.users;

        return {
          ...state,
          jobs,
        }
      },
    ),
    on(
      CloseJobComponentActions.closeJobSuccess,
      CancelAndCloseDialogActions.cancelAndCloseSuccess,
      JobHeaderActions.openJobSuccess,
      JobStatusDropdownActions.moveToNextStageSuccess,
      (state: JobsState, { updatedJob }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        jobs[updatedJob.id] = updatedJob;

        return {
          ...state,
          jobs,
        }
      },
    ),
    on(
      JobTagActions.setTagsSuccess,
      (state: JobsState, { tags, jobId }): JobsState => {
        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];
        if (job) {
          job.tags = tags;
        }

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      CreateEventDialogActions.createEventSuccess,
      InventoryActions.createVirtualEstimateSuccess,
      (state: JobsState, { createdEvent, jobId }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        job.events = [ ...job.events, createdEvent ];

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      CreateEventDialogActions.updateEventSuccess,
      OverviewTimelineActions.updateEventStatusSuccess,
      BookEventDialogActions.bookEventSuccess,
      BookEventDialogActions.rescheduleEventSuccess,
      EventItineraryActions.saveEventChangesSuccess,
      JobToolV3Actions.updateEventLocationsSuccess,
      (state: JobsState, { updatedEvent, jobId }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        const eventIndex = job.events.findIndex((e) => e.id === updatedEvent.id);

        if (eventIndex !== -1) {
          job.events[eventIndex] = {
            ...job.events[eventIndex],
            ...updatedEvent,
          };
        }

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(CancelAndCloseDialogActions.cancelEventsSuccess, (state: JobsState, { updatedEvents, jobId }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      for (const updatedEvent of updatedEvents) {
        const eventIndex = job.events.findIndex((e) => e.id === updatedEvent.id);

        if (eventIndex !== -1) {
          job.events[eventIndex] = {
            ...job.events[eventIndex],
            ...updatedEvent,
          };
        }
      }

      return {
        ...state,
        jobs,
      };
    }),
    on(
      mutateEventDialogUpdateEventSuccess,
      (state: JobsState, { updatedEvent }): JobsState => {

        let eventJobId: string;
        let eventIndex: number;

        for (const [jobId, job] of Object.entries(state.jobs)) {
          const idx = job?.events?.findIndex(e => e.id === updatedEvent.id) ?? -1;
          if (idx >= 0) {
            eventJobId = jobId;
            eventIndex = idx;
            break;
          }
        }

        if (!eventJobId) return state;

        const jobs = cloneDeep(state.jobs);

        const job = jobs[eventJobId];

        job.events.splice(eventIndex, 1, updatedEvent);

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      calendarEventDetailsUpdateEventSuccess,
      (state: JobsState, { updatedEvent }): JobsState => {

        let eventJobId: string;
        let eventIndex: number;

        for (const [jobId, job] of Object.entries(state.jobs)) {
          const idx = job?.events?.findIndex(e => e.id === updatedEvent.id) ?? -1;
          if (idx >= 0) {
            eventJobId = jobId;
            eventIndex = idx;
            break;
          }
        }

        if (!eventJobId) return state;

        const jobs = cloneDeep(state.jobs);

        const job = jobs[eventJobId];

        job.events.splice(eventIndex, 1, updatedEvent);

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      eventHelperUpdateEventSuccess,
      (state: JobsState, { updatedEvent }): JobsState => {

        let eventJobId: string;
        let eventIndex: number;

        for (const [jobId, job] of Object.entries(state.jobs)) {
          const idx = job?.events?.findIndex(e => e.id === updatedEvent.id) ?? -1;
          if (idx >= 0) {
            eventJobId = jobId;
            eventIndex = idx;
            break;
          }
        }

        if (!eventJobId) return state;

        const jobs = cloneDeep(state.jobs);

        const job = jobs[eventJobId];

        job.events.splice(eventIndex, 1, updatedEvent);

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      OverviewTimelineActions.deleteEventSuccess,
      (state: JobsState, { jobId, eventId }): JobsState => {

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        job.events = job.events.filter((e) => e.id !== eventId);

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      deleteEventDialogDeleteEventSuccess,
      (state: JobsState, { eventId }): JobsState => {

        let eventJobId: string;

        for (const [jobId, job] of Object.entries(state.jobs)) {
          if (job.events?.some((e) => e.id === eventId)) {
            eventJobId = jobId;
            break;
          }
        }

        if (!eventJobId) return state;

        const jobs = cloneDeep(state.jobs);

        const job = jobs[eventJobId];

        job.events = job.events.filter((e) => e.id !== eventId);

        return {
          ...state,
          jobs,
        };
      },
    ),
    on(
      JobToolSubscriptionActions.remoteJobUpdated,
      JobToolSubscriptionActions.localJobUpdated,
      NavigatorOutboxActions.jobInventoryUpdatedLocally,
      (state: JobsState, action): JobsState => {

      const { jobId, job } = action;

      const jobs = cloneDeep(state.jobs);

      const currentJob = jobs[jobId];

      if (!currentJob) { return state; }

      // Bug: remote job is missing comments
      const changes = cloneDeep(job);
      delete changes.comments;

      // No navigator-step guard here, deliberately.
      //
      // This block used to rewrite `navigatorCurrentStep` on our own echoes to stop them walking the
      // crew backwards. Two shapes were tried and both regressed: keeping whatever the store held
      // latched onto the launch sentinel and then rejected every legitimate advance, and narrowing it
      // to the sentinel let a whole replay through. Both were compensating for the crew's position
      // being derived from this shared object at all.
      //
      // That derivation is gone (#46). The navigator now holds the crew's step in the quickjs slice,
      // moved only by explicit actions, so a stale echo landing here is ordinary bookkeeping on a
      // shared object rather than something that can move a crew mid-move. Keeping a guard would
      // mean defending against a fixed cause, which is how this issue reached five layers.
      //
      // The cost is that the ops-facing workorders display is plain last-write-wins again, so a late
      // echo can briefly show an older step there. Cosmetic, ops-side only, and much reduced by
      // `updateNavigator`'s cache invalidation and by every navigator mutation now carrying
      // `generateMutationContext()`. See `docs/crew-navigator.md`.

      jobs[jobId] = {
        ...currentJob,
        ...changes,
      }

        return {
          ...state,
          jobs,
        }
      },
    ),
    // Immediately patch navigator fields on the event so the NgRx store (and
    // the localStorage persist:v2.1 snapshot) reflect the change without waiting
    // for the WebSocket subscription round-trip.
    // Optimistic step advance — patches only `navigatorCurrentStep`, leaving `navigatorData`
    // untouched, so the store stops trailing the UI by the length of the undo window (#31).
    on(WorkOrdersActions.navigatorStepAdvancedLocally, (state: JobsState, { jobId, eventId, navigatorCurrentStep }): JobsState => {
      const job = state.jobs[jobId];
      if (!job) { return state; }

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [jobId]: {
            ...job,
            events: (job.events ?? []).map(event =>
              event.id === eventId ? { ...event, navigatorCurrentStep } : event
            ),
          },
        },
      };
    }),
    // Data-only counterpart to `navigatorStepAdvancedLocally`: patches `navigatorData` and
    // deliberately does not touch `navigatorCurrentStep`, so a patch that resolves after the
    // crew has advanced cannot revert them (#46).
    on(WorkOrdersActions.navigatorDataUpdatedLocally, (state: JobsState, { jobId, eventId, navigatorData }): JobsState => {
      const job = state.jobs[jobId];
      if (!job) { return state; }

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [jobId]: {
            ...job,
            events: (job.events ?? []).map(event =>
              event.id === eventId ? { ...event, navigatorData } : event
            ),
          },
        },
      };
    }),
    on(
      WorkOrdersActions.navigatorUpdatedLocally,
      (state: JobsState, { jobId, eventId, navigatorCurrentStep, navigatorData }): JobsState => {
      const job = state.jobs[jobId];
      if (!job) { return state; }

        const updatedEvents = (job.events ?? []).map(event =>
          event.id === eventId
            ? { ...event, navigatorCurrentStep, navigatorData }
            : event
        );

        return {
          ...state,
          jobs: {
            ...state.jobs,
            [jobId]: { ...job, events: updatedEvents },
          },
        };
      },
    ),
    on(JobToolV3Actions.jobLoadingStateChanged, (state: JobsState, { jobId, loadingState }): JobsState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          [jobId]: loadingState,
        }
      }
    }),
    on(JobInvoiceActions.getInvoiceLinkSuccess, JobInvoiceActions.finalizeInvoiceSuccess, (state: JobsState, action): JobsState => {

      const { jobId, invoiceId } = action;

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) { return state; }

      const invoice = job.invoices.find((i) => i.id === invoiceId);

      if (!invoice) { return state; }

      if (invoice.openedAt) { return state; }

      const openedAegisSignPage = action.type === JobInvoiceActions.getInvoiceLinkSuccess.type
        && isAegisArtifact(invoice.artifacts?.[0]);

      if (openedAegisSignPage) { return state; }

      invoice.openedAt = currentTimeSeconds();

      return {
        ...state,
        jobs,
      }
    }),
    on(JobInvoiceActions.voidInvoiceSuccess, (state: JobsState, { jobId, invoiceId }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) { return state; }

      const voidedAt = currentTimeSeconds()

      const invoice = job.invoices.find((i) => i.id === invoiceId);

      if (invoice) {
        invoice.voidedAt = voidedAt;
      }

      for (const event of job.events) {
        event.invoices = (event.invoices || []).map((i) => i.id === invoiceId ? { ...i, voidedAt } : i);
      }

      for (const transaction of job.transactions) {
        if (transaction.invoice?.id === invoiceId) {
          delete transaction.invoice;
        }
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobInvoiceActions.deleteInvoiceSuccess, (state: JobsState, { jobId, invoiceId }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) { return state; }

      const deletedAt = currentTimeSeconds();

      const invoice = job.invoices.find((i) => i.id === invoiceId);

      if (invoice) {
        invoice.deletedAt = deletedAt;
      }

      for (const event of job.events) {
        event.invoices = (event.invoices || []).map((i) => i.id === invoiceId ? { ...i, deletedAt } : i);
      }

      for (const transaction of job.transactions) {
        if (transaction.invoice?.id === invoiceId) {
          delete transaction.invoice;
        }
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobInvoiceActions.createInvoiceSuccess, (state: JobsState, action): JobsState => addCreatedInvoice(state, action)),
    on(
      CreateTransactionDialogActions.chargeTransactionCreated,
      CreateTransactionDialogActions.squareCheckoutLinkSent,
      CreateTransactionDialogActions.abandonedTransactionCreated,
      (state: JobsState, { data }): JobsState => data.createTransaction.newInvoice
        ? addRaisedInvoice(state, data)
        // No invoice in this response (Square checkout links): the created
        // transaction itself must still reach the cached job.
        : addPendingTransaction(state, data.createTransaction.transaction),
    ),
    on(
      JobInvoiceActions.forceUpdateInvoiceSuccess,
      JobInvoiceActions.updateInvoiceSuccess,
      (state: JobsState, { jobId, invoiceId }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const invoice = (job.invoices || []).find((i) => i.id === invoiceId);

      if (!invoice) { return state; }

      invoice.artifacts = invoice.artifacts
        .map((artifact) => ({
          ...artifact,
          attributes: artifact.attributes.filter((att) => att !== 'outdated'),
        }));

      return {
        ...state,
        jobs,
      }
    }),
    on(JobInvoiceActions.exportInvoiceToQuickbooksSuccess, (state: JobsState, { jobId, invoiceId, quickbooksId }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) { return state; }

      const invoice = (job.invoices || [])
        .find((i) => i.id === invoiceId);

      if (!invoice) { return state; }

      invoice.metadata = {
        ...invoice.metadata,
        quickbooksId,
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobInvoiceActions.sendInvoiceSuccess, (state: JobsState, { jobId, invoiceId, updated }): JobsState => {

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) { return state; }

      const invoice = (job.invoices || [])
        .find((i) => i.id === invoiceId);

      if (!invoice) { return state; }

      if (invoice.sentAt) { return state; }

      invoice.sentAt = currentTimeSeconds();

      if (updated) {
        invoice.artifacts = invoice.artifacts
          .map((artifact) => ({
            ...artifact,
            attributes: artifact.attributes.filter((att) => att !== 'outdated'),
          }));
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobToolV3Actions.documentReadyToView, (state: JobsState, { jobId, artifact }): JobsState => {

      if (!jobId) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      if (!job) { return state; }

      const existingIndex = (job.artifacts || []).findIndex((a) => a.id === artifact.id);

      if (existingIndex >= 0) {
        job.artifacts[existingIndex] = { ...job.artifacts[existingIndex], ...artifact };
      } else {
        job.artifacts = [ ...(job.artifacts || []), artifact ];
      }

      const invoiceArtifact = (job.invoices || [])
        .flatMap((i) => i.artifacts)
        .find((a) => a.id === artifact.id);

      if (invoiceArtifact) {
        invoiceArtifact.signedUrl = artifact.signedUrl;
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobToolV3Actions.deleteTransactionSuccess, (state: JobsState, { jobId, transactionId }): JobsState => {

      if (!jobId) { return state; }

      const currentJob = state.jobs[jobId];

      if (!currentJob) { return state; }

      if (!currentJob.transactions.some((transaction) => transaction.id === transactionId)) {
        return state;
      }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const transaction = job.transactions.find((t) => t.id === transactionId);

      job.transactions = job.transactions.filter((t) => t.id !== transactionId);

      job.paidTotal = calculateJobTotals(job).paidTotal;

      if (transaction?.invoice && transaction.stage === 'paid') {

        const invoice = job.invoices.find((i) => i.id === transaction.invoice.id);

        if (invoice) {
          invoice.paidTotal = invoice.paidTotal - transaction.amount;
        }
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobToolV3Actions.applyTransactionSuccess, (state: JobsState, { jobId, transaction }): JobsState => {

      if (!jobId) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const transactionIndex = job.transactions.findIndex((t) => t.id === transaction.id);

      job.transactions.splice(transactionIndex, 1, transaction);

      if (transaction.invoice) {

        const invoice = job.invoices.find((i) => i.id === transaction.invoice.id);

        invoice.paidTotal = transaction.invoice.paidTotal;

        invoice.artifacts = invoice.artifacts.map((artifact) => ({
          ...artifact,
          attributes: [ ...artifact.attributes, 'outdated' ],
        }));
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(
      JobFinancialsActions.sendDocumentSuccess,
      JobEstimateActions.sendDocumentSuccess,
      CreateDocumentDialogActions.resendSucceeded,
      (state: JobsState, { jobId, artifactId }): JobsState => {

        if (!jobId || !state.jobs[jobId]) { return state; }

        const jobs = cloneDeep(state.jobs);

        const job = jobs[jobId];

        const artifact = job.artifacts?.find((a) => a.id === artifactId);

        if (!artifact) { return state; }

        artifact.attributes = artifact.attributes.map((att) => {

          if (att.startsWith('status')) {
            return 'status::sent';
          }

          return att;
        });

        if (!artifact.attributes.includes('status::sent')) {
          artifact.attributes.push('status::sent');
        }

        return {
          ...state,
          jobs,
        }
        },
    ),
    on(JobEstimateActions.deleteDocumentSuccess, (state: JobsState, { jobId, artifactId }): JobsState => {

      if (!jobId) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      job.artifacts = job.artifacts.filter((a) => a.id !== artifactId);

      return {
        ...state,
        jobs,
      }
    }),
    on(JobFinancialsActions.createArtifactSuccess, (state: JobsState, { jobId, artifact }): JobsState => {

      if (!jobId) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      job.artifacts = [ ...job.artifacts, artifact ];

      return {
        ...state,
        jobs,
      }
    }),
    on(CreateDocumentDialogActions.documentCreated, (state: JobsState, { jobId, artifact }): JobsState => {

      if (!jobId || !state.jobs[jobId]) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const artifactIndex = (job.artifacts || []).findIndex((a) => a.id === artifact.id);

      if (artifactIndex >= 0) {
        job.artifacts[artifactIndex] = { ...job.artifacts[artifactIndex], ...artifact };
      } else {
        job.artifacts = [ ...(job.artifacts || []), artifact ];
      }

      return {
        ...state,
        jobs,
      }
    }),
    on(JobEstimateActions.refreshDocumentSuccess, (state: JobsState, { jobId, artifact }): JobsState => {

      if (!jobId) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const artifactIndex = job.artifacts.findIndex((a) => a.id === artifact.id);

      job.artifacts.splice(artifactIndex, 1, artifact);

      return {
        ...state,
        jobs,
      }
    }),
    on(DispatchActions.updateCrewSuccess, (state: JobsState, { edits }): JobsState => {

      let jobs = state.jobs;

      for (const { eventId, addAttendees = [], removeAttendees = [] } of edits) {
        const jobId = Object.keys(jobs).find((id) => jobs[id]?.events?.some((e) => e.id === eventId));

        if (!jobId) { continue; }

        if (jobs === state.jobs) {
          jobs = cloneDeep(state.jobs);
        }

        const event = jobs[jobId].events.find((e) => e.id === eventId);

        if (!event) { continue; }

        const removeIds = new Set(removeAttendees.map((a) => a.user.id));
        event.attendees = (event.attendees || []).filter((a) => !removeIds.has(a.user?.id));

        for (const { user, role, locked } of addAttendees) {
          event.attendees.push({ user, role, locked });
        }
      }

      if (jobs === state.jobs) { return state; }

      return { ...state, jobs };
    }),
    on(JobToolV3Actions.shareEventSuccess, (state: JobsState, { jobId, eventId, zoneId }): JobsState => {

      if (!jobId) { return state; }

      const jobs = cloneDeep(state.jobs);

      const job = jobs[jobId];

      const nextSharedZones = zoneId ? [ zoneId ] : [];

      job.sharedZones = nextSharedZones;

      const event = job.events.find((e) => e.id === eventId);

      event.sharedZones = nextSharedZones;

      return {
        ...state,
        jobs,
      }
    }),
  ),
});

export function getJobFromLocalStorage(jobId: string) {
  return loadFromStorage('jobs', {})[jobId];
}

function addCreatedInvoice(
  state: JobsState,
  { invoice, eventIds, appliedTransactions }: {
    invoice: InvoiceWithArtifactsFragment;
    eventIds: string[];
    appliedTransactions: FullTransactionFragment[];
  },
): JobsState {

  const jobs = cloneDeep(state.jobs);

  const job = jobs[invoice.job.id];

  if (!job || job.invoices.some((i) => i.id === invoice.id)) { return state; }

  job.invoices = [ ...job.invoices, invoice ];

  for (const transaction of appliedTransactions) {
    const transactionIndex = job.transactions.findIndex((t) => t.id === transaction.id);

    if (transactionIndex === -1) { continue; }

    job.transactions.splice(transactionIndex, 1, transaction);
  }

  const invoicedEvents = eventIds
    .map((eventId) => job.events.find((e) => e.id === eventId))
    .filter(Boolean);

  for (const event of invoicedEvents) {
    event.invoices = event.invoices || [];
    event.invoices.push(invoice);
  }

  return {
    ...state,
    jobs,
  };
}

function addRaisedInvoice(state: JobsState, { createTransaction }: CreateTransactionMutation): JobsState {

  if (!createTransaction.newInvoice) { return state; }

  const { events, transactions, ...invoice } = createTransaction.newInvoice;

  return addCreatedInvoice(state, {
    invoice,
    eventIds: events.map((e) => e.id),
    appliedTransactions: transactions,
  });
}

/** CR (MR !2424 round 35): the Square checkout-link path stages a Pending
 *  transaction WITHOUT creating an invoice — the invoice branch returned
 *  early and the cached job kept stale transactions until refresh. The
 *  response still carries the created transaction (with job.id), so land it
 *  in the job's list (replace-in-place on a repeat) like the raising paths. */
function addPendingTransaction(state: JobsState, transaction: FullTransactionFragment | null | undefined): JobsState {

  if (!transaction?.job?.id) { return state; }

  const jobs = cloneDeep(state.jobs);
  const job = jobs[transaction.job.id];

  if (!job) { return state; }

  job.transactions = job.transactions.some((t) => t.id === transaction.id)
    ? job.transactions.map((t) => t.id === transaction.id ? transaction : t)
    : [...job.transactions, transaction];

  return {
    ...state,
    jobs,
  };
}
